<template>
  <footer class="tw-bg-[#111224]/[.02]">
    <section class="tw-container tw-mx-auto tw-py-8">
      <div class="">
        <NuxtLinkLocale to="/">
          <SvgoMenuHeaderLogo class="tw-h-[30px] md:tw-h-10 tw-fill-[#111224]" />
        </NuxtLinkLocale>
      </div>
      <div class="tw-h-px tw-bg-[#F1F1F1] tw-my-7"></div>
      <section class="tw-flex tw-flex-col md:tw-flex-row tw-justify-between tw-gap-y-7">
        <nav v-for="(item, index) in menuList" :key="index">
          <h6 class="tw-font-bold tw-leading-7">{{ $t(item.title) }}</h6>
          <div class="tw-gap-y-1 tw-flex tw-flex-col tw-mt-2">
            <div v-for="(menu, subIndex) in item.list"
              class="tw-flex tw-items-center tw-cursor-pointer tw-transition-all hover:tw-text-primary" :key="subIndex">
              <div v-if="menu.title === 'View all'">
                <NuxtLink :to="localeHref(menu.href)" class="text-highlight">{{ $t(menu.title) }}</NuxtLink>
              </div>
              <div v-else-if="['Support help center', 'TreasureBox'].includes(menu.title)"
                @click="navigateTo(menu.href)">
                {{ $t(menu.title) }}
              </div>
              <div v-else-if="menu.href.endsWith('.pdf')" @click="navigateTo(menu.href)">
                {{ $t(menu.title) }}
              </div>
              <div v-else-if="menu.key === 'twitter'" class="tw-flex tw-items-center" @click="navigateTo(menu.href)">
                <SvgoMenuX class="tw-w-5 tw-h-5 tw-mr-2" />
                {{ $t(menu.title) }}
              </div>
              <div v-else-if="menu.title === 'LinkedIn'" class="tw-flex tw-items-center" @click="navigateTo(menu.href)">
                <SvgoMenuLinkedIn class="tw-w-5 tw-h-5 tw-mr-2" />
                {{ $t(menu.title) }}
              </div>
              <div v-else-if="menu.title === 'Contact us'" @click="onTouched">
                {{ $t(menu.title) }}
              </div>
              <div v-else class="tw-max-w-44">
                <NuxtLink :to="localeHref(menu.href)">{{ $t(menu.title) }}</NuxtLink>
              </div>
            </div>
          </div>
        </nav>
      </section>
    </section>
  </footer>
  <footer class="tw-text-white tw-flex tw-flex-col tw-gap-3 tw-py-5 md:tw-py-7" v-if="isEu" :class="{
    'tw-bg-[#232323]': isSosoValue,
    'tw-bg-primary': !isSosoValue,
  }">
    <div
      class="tw-container tw-mx-auto tw-flex tw-flex-col tw-justify-between tw-items-center md:tw-items-start tw-text-sm tw-font-normal">
      <strong class="tw-mr-2 tw-mb-1">Risk Warning</strong>
      <div class=" md:tw-w-4/5">
        Crypto-assets are high-risk and can lose value quickly. You may lose all the money you invest. They are not regulated financial products, and you are not protected by deposit guarantee or investor compensation schemes. Please consider your financial situation carefully and seek independent advice if needed.
      </div>
    </div>
    <div
      class="tw-container tw-mx-auto tw-flex tw-flex-col tw-justify-between tw-items-center md:tw-items-start tw-text-sm tw-font-normal">
      <div class=" md:tw-w-4/5">
        Legend Financial Ireland Limited, trading as Legend Ireland, is regulated by the Central Bank of Ireland. Registered office: Block 1, Blanchardstown Corporate Park, Ballycoolin Road, Dublin 15, D15 AKK1, Ireland.
      </div>
    </div>
    <div
      class="tw-container tw-mx-auto tw-flex tw-flex-col md:tw-flex-row tw-justify-between tw-items-center tw-text-sm tw-gap-2 tw-leading-relaxed tw-font-normal">
      <p> © {{ new Date().getFullYear() }} {{ $t('legend_trading_dot') }} {{ $t('All rights reserved.') }} </p>
      <p>
        <NuxtLinkLocale class="tw-text-white" to="/eu/terms"> {{ $t('Terms of Use') }} </NuxtLinkLocale>
        |
        <NuxtLinkLocale class="tw-text-white" to="/eu/policy">{{ $t('Privacy Policy') }}</NuxtLinkLocale>
      </p>
    </div>
  </footer>
  <footer class="tw-text-white tw-flex tw-flex-col tw-gap-3 tw-py-5 md:tw-py-7" v-else :class="{
    'tw-bg-[#232323]': isSosoValue,
    'tw-bg-primary': !isSosoValue,
  }">
    <div
      class="tw-container tw-mx-auto tw-flex tw-flex-col tw-justify-between tw-items-center md:tw-items-start tw-text-sm tw-font-normal">
      <strong class="tw-mr-2">Risk Warning</strong>
      Cryptocurrency trading is high-risk and may result in the loss of some or all funds. Trade responsibly.
    </div>
    <div
      class="tw-container tw-mx-auto tw-flex tw-flex-col tw-justify-between tw-items-center md:tw-items-start tw-text-sm tw-font-normal">
      Legend Holdings US Inc. Registered No. 7477583. Registered Office: 251 Little Falls Drive, Wilmington, DE 19808.
    </div>
    <div
      class="tw-container tw-mx-auto tw-flex tw-flex-col md:tw-flex-row tw-justify-between tw-items-center tw-text-sm tw-gap-2 tw-leading-relaxed tw-font-normal">
      <p> © {{ new Date().getFullYear() }} {{ $t('legend_trading_dot') }} {{ $t('All rights reserved.') }} </p>
      <p>
        <NuxtLinkLocale class="tw-text-white" to="/terms"> {{ $t('Terms of Use') }} </NuxtLinkLocale>
        |
        <NuxtLinkLocale class="tw-text-white" to="/policy">{{ $t('Privacy Policy') }}</NuxtLinkLocale>
      </p>
    </div>
  </footer>
</template>
<script lang="ts" setup>
const localePath = useLocalePath()
const { locale } = useI18n()
const { isZa, isEu, isSosoValue } = useSite()

const menuList = computed(() => {
  if (unref(isZa)) {
    return [
      {
        title: 'Products',
        list: [
          {
            title: 'Legend OTC',
            href: '/legend-otc',
          },
          {
            title: 'Trade with us',
            href: '/kyc',
          },
        ],
      },
      {
        title: 'Company',
        list: [
          {
            title: 'About us',
            href: `/about`,
          },
          {
            title: 'Media kit',
            href: '/media-kit',
          },
          {
            title: 'Become our partner',
            href: `/partner`,
          },
          {
            title: 'Support help center',
            href: 'https://support.legendtrading.com/hc/en-us',
          },
          {
            title: 'Newsroom',
            href: `/newsroom`,
          },
        ],
      },
      {
        title: 'Follow',
        list: [
          {
            key: 'twitter',
            title: 'X(Twitter)',
            href: 'https://x.com/LegendTradingX',
          },
          {
            title: 'LinkedIn',
            href: 'https://www.linkedin.com/company/legend-trading',
          },
          {
            title: 'Contact us',
            href: '',
          },
        ],
      },
    ]
  }
  if (unref(isEu)) {
    return [
      {
        title: 'Products',
        list: [
          {
            title: 'Legend OTC',
            href: '/legend-otc',
          },
          {
            title: 'Legend Gateway',
            href: '/legend-gateway',
          },
          // {
          //   title: 'TreasureBox',
          //   href: 'https://www.treasurebox.com/',
          // },
        ],
      },
      {
        title: 'Showcases',
        list: [
          {
            title: 'KuCoin',
            href: `/cases/kucoin`,
          },
          {
            title: 'Phemex',
            href: `/cases/phemex`,
          },
          {
            title: 'View all',
            href: `/cases`,
          },
        ],
      },
      {
        title: 'Developers',
        list: [
          {
            title: 'Guidelines & Code',
            href: `/developers`,
          },
        ],
      },
      {
        title: 'Company',
        list: [
          {
            title: 'About us',
            href: `/about`,
          },
          {
            title: 'Media kit',
            href: '/media-kit',
          },
          {
            title: 'Become our partner',
            href: `/partner`,
          },
          {
            title: 'Support help center',
            href: 'https://support.legendtrading.com/hc/en-us',
          },
          {
            title: 'Blog',
            href: 'https://support.legendtrading.com/hc/en-us/sections/1260801558890-Announcements',
          },
          {
            title: 'Newsroom',
            href: `/newsroom`,
          },
        ],
      },
      {
        title: 'Legal',
        list: [
          {
            title: 'Risk Warning & Legal Disclaimer',
            href: '/eu/risk',
          },
          {
            title: 'Complaints Handling',
            href: '/eu/complaints',
          },
          {
            title: 'Terms of Use',
            href: '/eu/terms',
          },
          {
            title: 'Privacy Policy',
            href: '/eu/policy',
          },
          {
            title: 'Crypto-Asset Sustainability Disclosures',
            href: '/docx/EU/Legend-MiCA-Sustainability-Disclosure.pdf',
          },
        ]
      },
      {
        title: 'Follow',
        list: [
          {
            title: 'LinkedIn',
            href: 'https://www.linkedin.com/company/legend-trading',
          },
          {
            title: 'Contact us',
            href: '',
          },
        ],
      },
    ];
  }
  return [
    {
      title: 'Products',
      list: [
        {
          title: 'Legend OTC',
          href: '/legend-otc',
        },
        {
          title: 'Legend Gateway',
          href: '/legend-gateway',
        },
        // {
        //   title: 'TreasureBox',
        //   href: 'https://www.treasurebox.com/',
        // },
      ],
    },
    {
      title: 'Showcases',
      list: [
        {
          title: 'KuCoin',
          href: `/cases/kucoin`,
        },
        {
          title: 'Phemex',
          href: `/cases/phemex`,
        },
        {
          title: 'View all',
          href: `/cases`,
        },
      ],
    },
    {
      title: 'Developers',
      list: [
        {
          title: 'Guidelines & Code',
          href: `/developers`,
        },
      ],
    },
    {
      title: 'Company',
      list: [
        {
          title: 'About us',
          href: `/about`,
        },
        {
          title: 'Media kit',
          href: '/media-kit',
        },
        {
          title: 'Become our partner',
          href: `/partner`,
        },
        {
          title: 'Support help center',
          href: 'https://support.legendtrading.com/hc/en-us',
        },
        {
          title: 'Blog',
          href: 'https://support.legendtrading.com/hc/en-us/sections/1260801558890-Announcements',
        },
        {
          title: 'Newsroom',
          href: `/newsroom`,
        },
      ],
    },
    {
      title: 'Legal',
      list: [
        {
          title: 'Terms of Use',
          href: '/terms',
        },
        {
          title: 'Privacy Policy',
          href: '/policy',
        },
      ]
    },
    {
      title: 'Follow',
      list: [
            {
              key: 'twitter',
              title: 'X(Twitter)',
              href: 'https://x.com/LegendTradingX',
            },
            {
              title: 'LinkedIn',
              href: 'https://www.linkedin.com/company/legend-trading',
            },
            {
              title: 'Contact us',
              href: '',
            },
          ],
    }
  ]
});
const zenDesk = useZenDesk()
const onTouched = () => {
  zenDesk.onToggle()
}

const navigateTo = (herf: string) => {
  window.open(herf)
}

const localeHref = (url: string) => {
  if (isSosoValue.value) {
    if (url.startsWith('http')) {
      return url
    }
    return `https://legendtrading.com${localePath(url, locale.value)}`
  }
  return localePath(url, locale.value)
}
</script>
